'use client';
import { useCallback, useEffect } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';

import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import { CenteredContent } from '#/components/CenteredContent/CenteredContent';
import { IdealistLogo } from '#/components/Logo/IdealistLogo';
import { Text } from '#/components/Text/Text';
import { useAppConfig } from '#/config/AppConfigContext';
import { LoginFormContainer } from '#/containers/LoginFormContainer';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useIsFirstRenderRef } from '#/hooks/useIsFirstRenderRef';
import { useTrackPageView } from '#/hooks/useTrackPageView';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import { homePath } from '#/routing/routes';
import { colors } from '#/theme/theme';
import { allowRedirect } from '#/utils/allowRedirect';

export type AuthLoginPageLocationState = {
  title?: string;
  inviteId?: string;
  email?: string;
  redirectTo?: string | null;
  tmpStorage?: AuthApiTmpStorage;
};

type Props = {
  invite: ApiInvite | null | undefined;
};

export function AuthLoginPage({ invite }: Props) {
  const location = useLocation<AuthLoginPageLocationState>();

  const title = location.state?.title;
  const inviteId = location.state?.inviteId;
  const email = location.state?.email;
  const tmpStorage = location.state?.tmpStorage;

  const navigate = useNavigate();

  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();
  const { user } = useUser();

  const { isFirstRenderRef } = useIsFirstRenderRef();
  useEffect(() => {
    // Redirect to home if the user lands on the login page while logged in
    if (isFirstRenderRef.current && user)
      void navigate(homePath.with({ lang: locale }));
  }, [locale, isFirstRenderRef, navigate, user]);

  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('to') ?? location.state?.redirectTo;

  useTrackPageView('Login', {
    page_type: 'Login',
  });

  const handleRedirect = useCallback(
    (loggedInUser: ApiUser) => {
      const resolvedInviteId = invite?.id ?? location.state?.inviteId;

      let redirectToValue = null;

      if (resolvedInviteId) {
        redirectToValue = `/user-invites/${resolvedInviteId}`;
      } else if (redirectTo && !redirectTo.includes('#')) {
        // The server-side auth guard drops the fragment (e.g. #marketing),
        // but the browser carries it over to this page.
        redirectToValue = `${redirectTo}${window.location.hash}`;
      } else {
        redirectToValue = redirectTo;
      }

      if (!redirectToValue || !allowRedirect(redirectToValue, appConfig)) {
        redirectToValue = `/${loggedInUser.locale}/`;
      }

      if (loggedInUser.locale !== locale) {
        const regexp = new RegExp(`^/${locale}`);
        redirectToValue = redirectToValue.replace(
          regexp,
          `/${loggedInUser.locale}`,
        );
        void navigate(redirectToValue);
      }

      void navigate(redirectToValue);
    },
    [locale, redirectTo, location, navigate, invite, appConfig],
  );

  return (
    <CenteredContent>
      <div style={{ textAlign: 'center' }}>
        <IdealistLogo height={82} />
      </div>
      <Text
        style={{
          fontSize: '24px',
          fontWeight: 'semiBold',
          color: colors.mediumContentGrey,
          textAlign: 'center',
          marginBottom: '8px',
        }}
      >
        {title ??
          /* TRANSLATORS: login page title */
          i18n('Log In')}
      </Text>

      <LoginFormContainer
        invite={invite}
        analyticsTitle="[main] Login page"
        usageContext="page"
        redirectTo={redirectTo}
        email={email}
        inviteId={inviteId}
        tmpStorage={tmpStorage}
        onLogin={handleRedirect}
        initialWarning={null}
      />
    </CenteredContent>
  );
}
